/**
 * View-mode toggle — two-button pill in the dark header, near the user
 * menu (ProfileMenu).
 *
 * Renders ONLY when usePacks().canToggleMode is true, i.e. the tenant
 * has both a lead_gen_only pack (advisor) and an operator_crm pack
 * (senior_living) installed. Single-pack tenants get no toggle — single
 * pack = single view (the 5 provisioned Sky Advisor tenants never see
 * this control).
 *
 * No skypoint-ui segmented/pill primitive exists (only SkySwitch, a
 * boolean), so the pill is two styled buttons — consistent with the
 * Tailwind-on-dark-header pattern already used in header.tsx.
 *
 * Day-41+ post-ship fixes (3 bundled):
 *   1. Active-segment text uses `text-fg-1!` (important variant) —
 *      the parent toolbar at header.tsx:49 + :63 sets `text-white`, which
 *      under Tailwind v4's flat-utility cascade was winning over the
 *      arbitrary-color utility on the child. The `!` forces the active
 *      label dark-on-white instead of invisible white-on-white.
 *   2. onClick now ALSO navigates to a mode-appropriate landing route
 *      (CRM → /crm, Advisor → /), preserving the instanceid query.
 *      Without navigation, the sidebar swaps to the new mode but the URL
 *      stays on the old mode's page — user sees a CRM sidebar overlaid
 *      on an Advisor page (or vice versa). PackRouteGuard's mount-time
 *      effect only sets CRM mode on /crm/* deep-links; toggle-driven
 *      navigation is now explicit here.
 *   3. focus-visible: outline ring so keyboard users can see the focused
 *      segment (Tailwind v4 preflight zeros the native ring).
 */

import { useNavigate } from "react-router";
import { useMsalAuth } from "../../hooks/useMsalAuth";
import { usePacks } from "./PackContext";
import { ROUTES } from "../shared/constant";
import type { PackMode } from "../../store/api/packsApi";

// Displayed labels are "Sales" / "Marketing"; the underlying pack modes stay
// operator_crm / lead_gen_only (routing + state are keyed on mode, not label).
const SEGMENTS: { mode: PackMode; label: string }[] = [
  { mode: "operator_crm", label: "Sales" },
  { mode: "lead_gen_only", label: "Marketing" },
];

// CRM mode lands on /crm (CrmLandingRedirect bounces to /crm/day-planner).
// Advisor mode lands on the home dashboard.
const LANDING: Record<PackMode, string> = {
  operator_crm: ROUTES.CRM_LANDING,
  lead_gen_only: ROUTES.HOME,
};

export function ModeToggle() {
  const { canToggleMode, currentMode, setCurrentMode } = usePacks();
  const navigate = useNavigate();
  const { getQueryString } = useMsalAuth();

  if (!canToggleMode) return null;

  return (
    <div
      role="group"
      aria-label="View mode"
      className="flex items-center rounded-md border border-white/20 p-0.5 bg-white/5"
    >
      {SEGMENTS.map(({ mode, label }) => {
        const active = currentMode === mode;
        return (
          <button
            key={mode}
            type="button"
            aria-pressed={active}
            onClick={() => {
              if (active) return;
              setCurrentMode(mode);
              navigate(LANDING[mode] + getQueryString());
            }}
            className={
              "px-3 h-7 text-sm font-semibold rounded-[5px] whitespace-nowrap transition-colors " +
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 " +
              (active ? "bg-white text-fg-1!" : "text-white/70 hover:text-white hover:bg-white/10")
            }
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default ModeToggle;
